import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step",
    "stepIndicator",
    "prevButton",
    "nextButton",
    "submitButton",
    "counter",
    "checkbox",
    "filterResults",
    "selectedCount",
    "filteredCount"
  ]

  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 3 }
  }

  connect() {
    this.showStep(1)
    this.updateCounter()
  }

  // Navigation entre les étapes
  nextStep() {
    if (this.currentStepValue < this.totalStepsValue) {
      // Validation avant de passer à l'étape suivante
      if (this.validateCurrentStep()) {
        this.currentStepValue++
        this.showStep(this.currentStepValue)

        // Si on passe à l'étape 2, pré-cocher tous les utilisateurs
        if (this.currentStepValue === 2) {
          this.selectAllUsers()
        }
      }
    }
  }

  prevStep() {
    if (this.currentStepValue > 1) {
      this.currentStepValue--
      this.showStep(this.currentStepValue)
    }
  }

  goToStep(event) {
    const step = parseInt(event.currentTarget.dataset.step)
    if (step <= this.currentStepValue || this.canGoToStep(step)) {
      this.currentStepValue = step
      this.showStep(step)
    }
  }

  // Affiche l'étape courante
  showStep(step) {
    // Cacher toutes les étapes
    this.stepTargets.forEach((stepEl, index) => {
      if (index + 1 === step) {
        stepEl.classList.remove('hidden')
      } else {
        stepEl.classList.add('hidden')
      }
    })

    // Mettre à jour les indicateurs
    this.stepIndicatorTargets.forEach((indicator, index) => {
      const stepNumber = index + 1
      const circle = indicator.querySelector('.step-circle')
      const label = indicator.querySelector('.step-label')

      if (stepNumber < step) {
        // Étape complétée
        circle.classList.remove('bg-white', 'border-gray-300', 'text-gray-500', 'bg-blue-600', 'text-white')
        circle.classList.add('bg-green-500', 'text-white')
        label.classList.remove('text-gray-500', 'text-blue-600', 'font-semibold')
        label.classList.add('text-green-600')
        circle.innerHTML = '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>'
      } else if (stepNumber === step) {
        // Étape courante
        circle.classList.remove('bg-white', 'border-gray-300', 'text-gray-500', 'bg-green-500')
        circle.classList.add('bg-blue-600', 'text-white')
        label.classList.remove('text-gray-500', 'text-green-600')
        label.classList.add('text-blue-600', 'font-semibold')
        circle.textContent = stepNumber
      } else {
        // Étape future
        circle.classList.remove('bg-blue-600', 'text-white', 'bg-green-500')
        circle.classList.add('bg-white', 'border-gray-300', 'text-gray-500')
        label.classList.remove('text-blue-600', 'font-semibold', 'text-green-600')
        label.classList.add('text-gray-500')
        circle.textContent = stepNumber
      }
    })

    // Mettre à jour les boutons
    this.updateButtons()

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Met à jour l'affichage des boutons
  updateButtons() {
    const isFirstStep = this.currentStepValue === 1
    const isLastStep = this.currentStepValue === this.totalStepsValue

    // Bouton Précédent - caché sur la première étape
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.style.display = isFirstStep ? 'none' : 'inline-flex'
    }

    // Bouton Suivant - caché sur la dernière étape
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.style.display = isLastStep ? 'none' : 'inline-flex'
    }

    // Bouton Submit - visible uniquement sur la dernière étape
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.style.display = isLastStep ? 'inline-flex' : 'none'
    }
  }

  // Validation de l'étape courante
  validateCurrentStep() {
    if (this.currentStepValue === 1) {
      // Étape 1 : Vérifier qu'il y a au moins 1 utilisateur trouvé
      const filteredCount = this.getFilteredUsersCount()
      if (filteredCount === 0) {
        this.showNotification('❌ Aucun utilisateur ne correspond à vos filtres', 'error')
        return false
      }
      return true
    }

    if (this.currentStepValue === 2) {
      // Étape 2 : Vérifier qu'au moins 1 utilisateur est sélectionné
      const selectedCount = this.getSelectedCheckboxes().length
      if (selectedCount === 0) {
        this.showNotification('❌ Vous devez sélectionner au moins un destinataire', 'error')
        return false
      }
      return true
    }

    return true
  }

  // Vérifie si on peut aller à une étape
  canGoToStep(step) {
    // On ne peut aller qu'à l'étape suivante ou aux étapes précédentes
    return step <= this.currentStepValue + 1
  }

  // Sélectionne tous les utilisateurs (utilisé lors du passage à l'étape 2)
  selectAllUsers() {
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = true
    })
    this.updateCounter()
  }

  // Gestion de la sélection (réutilise user_selector_controller logic)
  selectAll() {
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = true
    })
    this.updateCounter()
    this.showNotification('✓ Tous les utilisateurs ont été sélectionnés', 'success')
  }

  selectNone() {
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false
    })
    this.updateCounter()
    this.showNotification('✗ Tous les utilisateurs ont été désélectionnés', 'info')
  }

  invertSelection() {
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = !checkbox.checked
    })
    this.updateCounter()
    this.showNotification('⇄ Sélection inversée', 'info')
  }

  // Met à jour le compteur de destinataires
  updateCounter() {
    const selectedCount = this.getSelectedCheckboxes().length

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = selectedCount
    }

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = selectedCount
    }
  }

  // Récupère les checkboxes sélectionnées
  getSelectedCheckboxes() {
    return this.checkboxTargets.filter(checkbox => checkbox.checked)
  }

  // Récupère le nombre d'utilisateurs trouvés par les filtres
  getFilteredUsersCount() {
    if (this.hasFilteredCountTarget) {
      return parseInt(this.filteredCountTarget.textContent) || 0
    }
    return this.checkboxTargets.length
  }

  // Affiche une notification toast
  showNotification(message, type = 'info') {
    const notification = document.createElement('div')

    const colors = {
      success: 'bg-green-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500',
      error: 'bg-red-500'
    }

    notification.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${colors[type] || colors.info} text-white font-medium`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.classList.add('opacity-0', 'transition-opacity', 'duration-500')
      setTimeout(() => notification.remove(), 500)
    }, 3000)
  }
}
